import { useState } from 'react';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Collapse,
  Descriptions,
  Form,
  FormInstance,
  List,
  Modal,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
  notification,
} from 'antd';
import {
  DataFeedConfig,
  GetDataFeedConfigResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import { useListTeamClients } from 'client/modules/external-api/hooks/queries/external-api';
import { cleanRpcError } from 'client/modules/common/utils/rpcError';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useUpdateDataFeedConfigMutation } from '../services/hooks';
import { DataFeedRoutes } from '../routesHelper';
import { JSONRequestFilters, formatMB } from './JSONRequestFilters';
import { DataFeedFileTypeLabels } from './DataFeedFiles';
import {
  FeedOptionalColumns,
  formatOptionalColumnCount,
} from './OptionalColumnsSummary';
import {
  DataFeedFormField,
  DatasetLabels,
  JSONRequest,
  fileTypeToNumber,
  isSupportedDataset,
} from './DataFeedForm';
import {
  FrequencyOptionLabels,
  isSupportedFrecuencyType,
} from './DataFeedFrequencyTabForm';
import {
  DestinationTypeLabels,
  isSupportedDestinationType,
} from './DataFeedDestinationTabForm';

const { Text } = Typography;
const { Panel } = Collapse;

const SUBMIT_ERROR_KEY = 'data-feed-update-error';

type TabKey =
  | 'feed'
  | 'apiClient'
  | 'dataset'
  | 'files'
  | 'apiRequest'
  | 'destination'
  | 'frequency';

const datasetLabel = (feed: DataFeedConfig): string =>
  isSupportedDataset(feed.dataset)
    ? DatasetLabels[feed.dataset]
    : String(feed.dataset);

const frequencyLabel = (feed: DataFeedConfig): string =>
  isSupportedFrecuencyType(feed.frequency)
    ? FrequencyOptionLabels[feed.frequency]
    : String(feed.frequency);

const destinationLabel = (feed: DataFeedConfig): string => {
  const type = feed.destination?.destination_type;
  if (type !== undefined && isSupportedDestinationType(type)) {
    return DestinationTypeLabels[type];
  }
  return '—';
};

const ApiRequestTab = ({
  feed,
  form,
}: {
  feed: DataFeedConfig;
  form: FormInstance;
}) => {
  const handleDownload = () => {
    const blob = new Blob([feed.id_orgs_request], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `datafeed-${feed.id_data_feed}-api-request.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const savedSize = feed.id_orgs_request
    ? formatMB(new Blob([feed.id_orgs_request]).size)
    : '—';

  return (
    <Form form={form} layout="vertical" className="w-full">
      <Space direction="vertical" size="middle" className="w-full">
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Saved request size">
            {savedSize}
          </Descriptions.Item>
        </Descriptions>

        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          disabled={!feed.id_orgs_request}
        >
          Download current JSON
        </Button>

        <JSONRequestFilters />
      </Space>
    </Form>
  );
};

const ReadOnlyTab = ({ children }: { children: React.ReactNode }) => (
  <Space direction="vertical" size="middle" className="w-full">
    {children}
  </Space>
);

export const EditDataFeedTabs = ({
  feed,
  idTeam,
}: {
  feed: DataFeedConfig;
  idTeam: string;
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('apiRequest'); // starts with only editable tab
  const [form] = Form.useForm();
  const jsonRequest: JSONRequest | undefined = Form.useWatch(
    [DataFeedFormField.filters, 'jsonRequest'],
    form
  );
  const payload = jsonRequest?.payload;

  const { data: teamClients } = useListTeamClients(Number(idTeam));
  const client = teamClients?.clients.find(
    (c) => c.client_id === feed.client_id
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useUpdateDataFeedConfigMutation();
  const [modal, contextHolder] = Modal.useModal();

  const backToList = () => navigate(`${DataFeedRoutes.DataFeed}/${idTeam}`);

  const handleCancel = () => {
    if (!payload) {
      backToList();
      return;
    }
    modal.confirm({
      title: 'Discard changes?',
      content: 'You have unsaved changes. Discard them?',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: backToList,
    });
  };

  const handleSave = () => {
    if (!payload) return;
    mutate(
      { id_data_feed: feed.id_data_feed, api_request: payload },
      {
        onError: (err: unknown) => {
          notification.error({
            key: SUBMIT_ERROR_KEY,
            message: 'Failed to update the Data Feed',
            description: cleanRpcError(err),
            placement: 'top',
            duration: 10,
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries(['ListDataFeedConfigResponse', idTeam]);
          // Patch cache with the request we hold; refetch would be ~50 MB.
          queryClient.setQueryData<GetDataFeedConfigResponse>(
            ['GetDataFeedConfig', feed.id_data_feed],
            (prev) =>
              prev?.data_feed_config
                ? {
                    ...prev,
                    data_feed_config: {
                      ...prev.data_feed_config,
                      id_orgs_request: payload,
                      last_update_time: new Date().toISOString(), // estimated; server sets real value
                    },
                  }
                : (prev as GetDataFeedConfigResponse)
          );
          message.success({
            content: `Data Feed ${feed.id_data_feed} updated successfully`,
            style: { marginTop: '3vh' },
          });
          form.resetFields();
        },
      }
    );
  };

  const items = [
    {
      key: 'feed',
      label: 'Feed',
      children: (
        <ReadOnlyTab>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Feed ID">
              {feed.id_data_feed}
            </Descriptions.Item>
            <Descriptions.Item label="Name">
              {feed.data_feed_name}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={feed.is_active ? 'green' : 'red'}>
                {feed.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Last updated">
              {feed.last_update_time
                ? new Date(feed.last_update_time).toLocaleString('en-US')
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
    {
      key: 'apiClient',
      label: 'API Client',
      children: (
        <ReadOnlyTab>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Team">
              {client?.team_name ? `${client.team_name}` : idTeam}
            </Descriptions.Item>
            <Descriptions.Item label="Client ID">
              {feed.client_id}
            </Descriptions.Item>
            <Descriptions.Item label="Available credits">
              {client ? client.available_credits : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Expiration date (UTC)">
              {client?.expiration_date
                ? `${new Date(client.expiration_date).toLocaleString('en-US', {
                    timeZone: 'UTC',
                  })}`
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
    {
      key: 'dataset',
      label: 'Dataset',
      children: (
        <ReadOnlyTab>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Dataset">
              {datasetLabel(feed)}
            </Descriptions.Item>
            <Descriptions.Item label="Files">
              {feed.deltas ? 'Full and deltas files' : 'Full files only'}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
    {
      key: 'files',
      label: 'Dataset Files',
      children: (
        <ReadOnlyTab>
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ width: 160 }}
          >
            <Descriptions.Item label="File types">
              {feed.file_types.length ? (
                <List
                  size="small"
                  dataSource={feed.file_types}
                  renderItem={(ft) => {
                    const entry =
                      feed.optional_columns_by_file_type?.[
                        fileTypeToNumber(ft)
                      ];
                    const columns = entry && Object.keys(entry.columns).sort();
                    return (
                      <List.Item>
                        <div className="w-full">
                          <div>{DataFeedFileTypeLabels[ft]}</div>
                          {columns && (
                            <Collapse className="ml-6 mt-1">
                              <Panel
                                key="columns"
                                header="Optional columns"
                                extra={formatOptionalColumnCount(
                                  columns.length
                                )}
                              >
                                <FeedOptionalColumns columns={columns} />
                              </Panel>
                            </Collapse>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
    {
      key: 'apiRequest',
      label: (
        <span>
          <EditOutlined className="!mr-1" />
          API Request
        </span>
      ),
      children: <ApiRequestTab feed={feed} form={form} />,
    },
    {
      key: 'destination',
      label: 'Destination',
      children: (
        <ReadOnlyTab>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Destination">
              {destinationLabel(feed)}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
    {
      key: 'frequency',
      label: 'Frequency',
      children: (
        <ReadOnlyTab>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Frequency">
              {frequencyLabel(feed)}
            </Descriptions.Item>
          </Descriptions>
        </ReadOnlyTab>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" className="w-full">
      {contextHolder}
      <Text type="secondary">
        <EditOutlined /> Only the API Request can be edited. Other tabs are
        read-only.
      </Text>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        items={items.map((item) => ({
          ...item,
          disabled: isLoading && item.key !== activeTab,
        }))}
      />
      <Space>
        <Button onClick={handleCancel}>Cancel</Button>
        {activeTab === 'apiRequest' && (
          <Button
            type="primary"
            loading={isLoading}
            disabled={!payload}
            onClick={handleSave}
          >
            Save changes
          </Button>
        )}
      </Space>
    </Space>
  );
};
